import { settingsP3 } from "./settings-p3";
import { settingsZoho } from "./settings-zoho";
/** Settings screen (tenant/settings.html): workspace, profile, developer access, security, privacy. */
export const settings = {
  title: "Settings",
  subtitle: "Workspace, your profile, developer access, security and data privacy.",
  navLabel: "Settings sections",
  panels: {
    org: "Organization",
    profile: "Profile",
    api: "API keys",
    webhooks: "Webhooks",
    integrations: "Integrations",
    "secure-launch": "Secure launch",
    security: "Security",
    privacy: "Data & privacy",
    audit: "Audit log",
    notifications: "Notifications",
    domains: "Domains",
    attributes: "Attributes",
    sla: "SLA policies",
    roles: "Roles & permissions",
  },
  /** Under four menu entries: which way the traffic goes (API keys in, webhooks and connectors out, sign-in via a provider). */
  direction: {
    inbound: "Inbound",
    outboundEvents: "Outbound events",
    outboundCalls: "Outbound calls",
    signIn: "Sign-in",
  },
  attributes: settingsP3.attributes,
  sla: settingsP3.sla,
  save: "Save",
  saveChanges: "Save changes",
  discard: "Discard",
  colActions: "Actions",
  copyFailed: "Couldn't copy. Select the text and copy it manually.",
  loadError: "This section couldn't load.",
  loadErrorBody: "Check your connection and try again. Nothing was changed.",
  forbiddenTitle: "You don't have access to this section",
  forbiddenBody: "Your role doesn't include workspace settings. Your own profile and notifications are still available.",
  planGateTitle: "Not included in your plan",
  planGateBody: "Upgrade your plan to use this part of Settings.",
  region: {
    title: "Billing country & currency",
    subtitle: "Sets the tax on your invoices and the currency you pay in.",
    country: "Billing country",
    countryHint: "Philippines: 12% VAT. Other countries: VAT 0% (zero-rated export of services).",
    currency: "Billing currency",
    currencyHint: "A change applies when your next billing period starts, never mid-period.",
    currencies: {
      PHP: "Philippine peso (PHP)",
      USD: "US dollar (USD)",
    },
    tax: "Tax on invoices: {{label}}.",
    pending: "Switching to {{currency}} on {{date}}.",
    save: "Save billing region",
    saved: "Billing region saved",
    savedLater: "Saved. The new currency applies from {{date}}.",
    noAccess: "Only people who can view billing see and change the billing country and currency.",
    loadError: "The billing region couldn't load. Try again in a moment.",
  },
  org: {
    title: "Organization",
    subtitle: "Shown to visitors in the widget, web app and emails",
    name: "Organization name",
    nameRequired: "Enter your organization's name",
    website: "Website",
    websiteInvalid: "Use a full https:// address",
    industry: "Industry",
    industryNone: "Not set",
    timezone: "Timezone",
    timezones: {
      Asia_Manila: "Asia/Manila (GMT+8)",
      Asia_Singapore: "Asia/Singapore (GMT+8)",
      UTC: "UTC",
    },
    language: "Default language",
    languageHint: "The assistant still replies in the visitor's language.",
    languages: {
      en: "English",
      fil: "Filipino",
    },
    workspaceUrl: "Workspace URL",
    workspaceUrlHint: "Contact support to change your workspace address.",
    address: "Address",
    city: "City",
    saved: "Organization settings saved",
    uploadLogo: "Upload logo",
    removeLogo: "Remove",
    logoHint: "Square PNG, JPEG or WebP, at least 256 × 256 px, up to 2 MB.",
    logoAlt: "{{name}} logo",
    logoUploaded: "Logo uploaded",
    logoRemoved: "Logo removed. Initials will be shown.",
    logoUploadFailed: "The logo couldn't be uploaded. Try again.",
    logoBadType: "Use a PNG, JPEG or WebP image.",
    logoTooLarge: "That image is larger than 2 MB.",
  },
  profile: {
    title: "Your profile",
    photoHint: "Your initials are shown next to your replies and in the team list.",
    name: "Full name",
    nameRequired: "Enter your name",
    jobTitle: "Job title",
    email: "Email",
    emailHint: "Changing your email requires re-verification. Ask a workspace Owner for help.",
    mobile: "Mobile (for handoff alerts)",
    mobileInvalid: "Enter a valid mobile number",
    save: "Save profile",
    saved: "Profile updated",
  },
  password: {
    title: "Change password",
    current: "Current password",
    currentRequired: "Enter your current password",
    new: "New password",
    newPlaceholder: "At least {{count}} characters",
    tooShort: "Use at least {{count}} characters",
    confirm: "Confirm new password",
    mismatch: "The passwords don't match",
    submit: "Update password",
    changed: "Password changed. Other sessions were signed out.",
  },
  twoFactor: {
    title: "Two-factor authentication",
    on: "Enabled",
    off: "Not set up",
    onHint: "Authenticator app",
    offHint: "Add a second step to sign-in with an authenticator app.",
    setUp: "Set up 2FA",
    newDevice: "Set up new device",
    cancelSetup: "Cancel setup",
    step1: "Open your authenticator app, such as Google Authenticator, Microsoft Authenticator, 1Password or Authy.",
    step2: "Scan this QR code, or enter the key manually.",
    step3: "Enter the 6-digit code from the app.",
    qrLabel: "QR code for setting up two-factor authentication",
    keyLabel: "Setup key",
    copyKey: "Copy key",
    sameDeviceHint: "Setting up on this phone? Add the account straight to your authenticator app.",
    openApp: "Open in authenticator app",
    preparing: "Preparing your setup key",
    loadErrorTitle: "We couldn't start setup",
    codeLabel: "6-digit code",
    verify: "Verify",
    enabledToast: "Two-factor authentication is on",
    deviceVerified: "New authenticator device verified",
    codesLeft_one: "Backup codes: {{count}} left",
    codesLeft_other: "Backup codes: {{count}} left",
    regenerate: "Regenerate codes",
    regenerateTitle: "Regenerate backup codes",
    regenerateBody: "Your old backup codes stop working. Enter a code from your authenticator app to continue.",
    regenerated: "New backup codes generated",
    codesTitle: "Save your backup codes",
    codesBody: "Each code works once if you lose your phone. This is the only time they're shown.",
    codesDone: "I've saved them",
    download: "Download .txt",
    disable: "Turn off 2FA",
    disableTitle: "Turn off two-factor authentication?",
    disableBody: "Your account will be protected by your password only. Enter your password and a current code.",
    disabled: "Two-factor authentication turned off",
  },
  sessions: {
    title: "Active sessions",
    revokeOthers: "Sign out all other sessions",
    othersRevoked: "Signed out of your other sessions",
    revoke: "Revoke",
    revokeOne: "Revoke session on {{device}}",
    revoked: "Session revoked",
    thisDevice: "This device",
    activeNow: "active now",
    unknownDevice: "Unknown device",
    loadError: "Sessions couldn't load.",
    emptyTitle: "No other sessions",
    emptyBody: "You're only signed in here.",
    showAll: "Show all {{count}} sessions",
    showFewer: "Show fewer",
  },
  api: {
    title: "API keys",
    subtitle: "Use these to call the API from your own systems. Keep them on your server, never in browser code.",
    directionLead: "Use these keys when your systems call Sagot. To have Sagot call your systems (products, orders, assets…), use ",
    directionLink: "Connectors",
    directionTail: ".",
    createKey: "Create key",
    createTitle: "Create API key",
    name: "Name",
    namePlaceholder: "e.g. Customer portal",
    nameRequired: "Give the key a name",
    scopes: "Scopes",
    scopeHelp: {
      chat_write: "Send messages to agents and get answers",
      knowledge_write: "Reserved: manage knowledge sources (no endpoint accepts it yet)",
      analytics_read: "Read analytics: agent reports and the summary (GET /api/v1/analytics/*)",
      conversations_read: "Read messages and staff replies of conversations started through the API (contains personal data)",
      workflows_read: "Read your workflows and their runs (run counts, goals and outcomes; no workflow steps)",
      tickets_read: "Read tickets and their public messages (contains personal data)",
      tickets_write: "Open tickets for your contacts (included from the Pro plan)",
      organisations_read: "Read client organisations and contacts (contains personal data)",
      organisations_write: "Create or update client organisations and contacts by your own IDs",
      catalogue_read: "Read the products you sell, with prices and stock as clients see them",
      orders_read: "Read orders and their status",
      orders_write: "Create orders for a client; they follow the same confirmation and approval rules",
      launch_write: "Start the hosted chat for a customer who is signed in on your site (Secure launch must be on)",
      customers_read: "Look up a customer's tier, status and attributes by your reference (Secure launch must be on)",
      customers_write: "Send a customer's tier, status and attributes, and end their chat sessions (Secure launch must be on)",
    },
    expires: "Expires",
    expiry: {
      never: "Never",
      year: "In 1 year",
      quarter: "In 90 days",
    },
    create: "Create key",
    secretTitle: "Copy your new key",
    secretLabel: "API key",
    secretWarningStrong: "This is the only time you'll see this key.",
    secretWarning: "Store it in your server's environment variables. If you lose it, revoke it and create a new one.",
    saved: "I've saved it",
    colName: "Name",
    colKey: "Key",
    colScopes: "Scopes",
    colCreated: "Created",
    colLastUsed: "Last used",
    neverUsed: "Never",
    revoke: "Revoke",
    revokeOne: "Revoke {{name}}",
    revoked: "Revoked",
    expired: "Expired",
    revokedToast: "API key revoked. Requests using it now return 401.",
    emptyTitle: "No API keys yet",
    emptyBody: "Create a key to connect your portal, CRM or reporting tools.",
    quickStart: "Quick start",
    sampleQuestion: "What are your office hours?",
    rateLimit: "Rate limit: {{count}} requests/min per key",
    reference: "API reference",
    // Key hardening (M29 SEC-005): IP allowlist, in-place rotation, request signing.
    createdOn: "Created {{date}}",
    colAllowed: "Allowed IPs",
    anyAddress: "Any address",
    moreRanges: "+{{count}} more",
    security: "Security",
    securityOne: "Security settings for {{name}}",
    rotate: "Rotate",
    rotateOne: "Rotate {{name}}",
    retires: "Old secret retires in {{time}}",
    signingRequiredBadge: "Signing required",
    signingOptionalBadge: "Signing on",
    createAllowlist: "Allowed IP addresses",
    createAllowlistHint: "Optional. Leave empty to accept calls from any address.",
    remaining: {
      hm: "{{hours}} h {{minutes}} min",
      m: "{{minutes}} min",
      soon: "under a minute",
      over: "now",
    },
    allowlist: {
      title: "IP allowlist and signing",
      description: "Rules for {{name}}. A change applies to the next call.",
      sectionIps: "Allowed IP addresses",
      hint: "Calls from any other address get 403 ip_not_allowed. Leave the list empty to accept any address.",
      note: "Sagot reads the caller's address from your proxy chain's trusted hops only, so a forged X-Forwarded-For header does not help.",
      add: "Add",
      placeholder: "203.0.113.0/24 or 2001:db8::/32",
      empty: "No restriction: any address can use this key.",
      count: "{{count}} of {{max}}",
      remove: "Remove {{item}}",
      duplicate: "{{item}} is already in the list",
      full: "At most {{max}} addresses or ranges.",
      added: "Added {{item}}",
      removed: "Removed {{item}}",
      invalid: "Enter an IPv4 or IPv6 address or range, like 203.0.113.0/24.",
      save: "Save allowlist",
      saved: "Allowlist saved.",
    },
    signing: {
      title: "Request signing",
      off: "Off",
      optional: "On, optional",
      required: "Required",
      body: "Sign each call with an HMAC so a stolen key alone is not enough. Send {{header}}: t=…,v1=… over the method, path and body; requests older than 5 minutes or sent twice are refused.",
      docs: "How to sign requests",
      require: "Refuse requests that are not signed",
      turnOn: "Turn on signing",
      reissue: "Issue a new secret",
      turnOff: "Turn off signing",
      offToast: "Request signing is off.",
      issuedTitle: "Copy your signing secret",
      secretLabel: "Signing secret",
      warningStrong: "This is the only time you'll see this secret.",
      warning: "Store it next to the API key on your server. Issuing a new one replaces this one at once.",
      saved: "I've saved it",
    },
    rotateDialog: {
      title: "Rotate {{name}}",
      intro: "A new secret is issued for this key. The current secret keeps working for {{hours}} hours so you can deploy the new one without downtime, then it is refused.",
      again: "A rotation is already running. Rotating again ends the earlier secret now.",
      confirm: "Rotate key",
      doneTitle: "Copy your new key",
      secretLabel: "New API key",
      warningStrong: "This is the only time you'll see this key.",
      warning: "Put it on your server. The old secret keeps working until the countdown ends.",
      countdown: "Old secret stops working in",
      saved: "I've saved it",
    },
  },
  webhooks: {
    title: "Endpoints",
    subtitle: "We POST JSON to these URLs when events happen",
    add: "Add endpoint",
    addTitle: "Add webhook endpoint",
    editTitle: "Edit webhook endpoint",
    url: "Endpoint URL",
    urlPlaceholder: "https://your-server.example.com/webhooks",
    urlInvalid: "Use a public https:// URL",
    events: "Events",
    format: "Message format",
    formats: {
      sagot: "Sagot JSON",
      discord: "Discord",
      slack: "Slack",
    },
    formatHint: {
      sagot: "A signed JSON event, for your own server.",
      discord: "A readable message for a Discord channel webhook.",
      slack: "A readable message for a Slack incoming webhook.",
    },
    formatSuggest: "This looks like a {{format}} webhook URL.",
    formatUse: "Use the {{format}} format",
    delivery: {
      label: "When to send",
      modes: { events: "Subscribed events", filtered: "Filtered events", workflows: "Only from my workflows" },
      sentBy: "Sent by the Developer webhooks workflow.",
      sentByLink: "Open the workflow",
      paused: {
        title: "Event delivery is paused",
        body: "The Developer webhooks workflow is paused, so no event is sent to any endpoint. Test sends and your own workflows' Send webhook steps still work.",
        action: "Open the workflow to resume it",
        badge: "Events paused",
      },
      hints: {
        events: "Every event of the chosen types, from every agent and channel.",
        filtered: "The chosen events, only from the channels and message senders you pick.",
        workflows: "Nothing is sent automatically. A workflow's Send webhook or Data connector step decides when and what.",
      },
      eventsRequired: "Choose at least one event, or send only from workflows.",
      connectorAuthHint: "Connector authentication (a key a workflow's Data connector sends) is available on endpoints set to Only from my workflows.",
      channels: "Only these channels",
      roles: "Only messages from",
      roleNames: { visitor: "Visitors", assistant: "The assistant", staff: "Staff" },
      filtersHint: "Leave a list empty for no limit. Automatic lines (\"waiting for the team\", Messenger prompts) are never sent.",
    },
    formatMismatch: {
      discord: "The Discord format needs a Discord webhook URL (https://discord.com/api/webhooks/…).",
      slack: "The Slack format needs a Slack incoming webhook URL (https://hooks.slack.com/services/…).",
    },
    enabled: "Endpoint enabled",
    save: "Save endpoint",
    saved: "Webhook endpoint saved",
    deleted: "Webhook endpoint deleted",
    deleteOne: "Delete endpoint {{url}}",
    edit: "Edit",
    editOne: "Edit endpoint {{url}}",
    healthy: "Healthy",
    failures_one: "{{count}} failure (24h)",
    failures_other: "{{count}} failures (24h)",
    disabled: "Disabled",
    sendTest: "Send test",
    testOk: "Test event sent → {{code}} in {{ms}} ms",
    testFailed: "Test event failed → {{code}} after {{ms}} ms",
    testReceiver: "The receiver said: {{excerpt}}",
    rotate: "Rotate secret",
    rotateTitle: "Rotate signing secret",
    rotateBody: "A new secret is generated for this endpoint. The old one keeps working for 24 hours so you can update your server.",
    rotated: "Signing secret rotated. The old secret works for 24 more hours.",
    secretTitle: "Copy the signing secret",
    secretLabel: "Signing secret",
    secretWarning: "This is the only time you'll see this secret. Store it on your server to verify each request.",
    signingTitle: "Signing secrets",
    signingBodyLead: "Each request has a",
    signingBodyTail: "header (HMAC-SHA256 of the body) made with the endpoint's own secret. Verify it before trusting the payload. Secrets are shown once when an endpoint is created or rotated.",
    connectorRequest: "Data connector request",
    connectorAuth: {
      title: "Connector authentication",
      notSet: "Not set",
      hint: "A header the Data connector sends with every request to this endpoint, such as Authorization or X-Api-Key. The secret is stored encrypted and is never shown again: you can replace or remove it.",
      set: "Set authentication",
      replace: "Replace",
      remove: "Remove",
      setTitle: "Set connector authentication",
      replaceTitle: "Replace connector authentication",
      dialogBody: "The value is sent in this header with every connector request. We keep only the last four characters to show.",
      header: "Header name",
      headerHint: "For example Authorization or X-Api-Key.",
      headerPlaceholder: "Authorization",
      headerProblems: { empty: "Enter the header name.", invalid: "Use letters, numbers and - only (up to 40 characters).", forbidden: "This header is set by Sagot or the connection and can't be used." },
      value: "Secret value",
      valueHint: "Paste the whole value, such as Bearer abc123. It can't be read back.",
      valueProblems: { empty: "Enter the secret value.", long: "The value is too long (up to {{max}} characters).", control: "The value can't contain line breaks or control characters." },
      save: "Save authentication",
      saved: "Connector authentication saved",
      removed: "Connector authentication removed",
      removeTitle: "Remove connector authentication?",
      removeBody: "Data connector requests to this endpoint will be sent without the header until you set it again. The saved value can't be recovered.",
    },
    deliveries: "Recent deliveries",
    deliveriesError: "Deliveries couldn't load.",
    endpointFilter: "Endpoint",
    retryFailed: "Retry failed",
    redeliver: "Redeliver",
    redeliverOne: "Redeliver {{event}}",
    redelivering_one: "Retrying {{count}} delivery…",
    redelivering_other: "Retrying {{count}} deliveries…",
    retryOf: "retry {{attempt}}/{{max}}",
    receiverSaid: "Receiver response",
    nextRetry: "Next retry {{time}}.",
    notRetried: "Not retried. Fix the endpoint, then redeliver.",
    ms: "{{value}} ms",
    colEvent: "Event",
    colEndpoint: "Endpoint",
    colStatus: "Status",
    colDuration: "Duration",
    colTime: "Time",
    status: {
      pending: "Pending",
      succeeded: "Delivered",
      failed: "Failed",
      retrying: "Retrying",
    },
    noDeliveriesTitle: "No deliveries yet",
    noDeliveriesBody: "Send a test event to check that your endpoint answers.",
    emptyTitle: "No webhook endpoints",
    emptyBody: "Add an endpoint to get conversations, handoffs and leads in your own systems as they happen.",
  },
  integrations: {
    connect: "Connect",
    disconnect: "Disconnect",
    connectTitle: "Connect {{name}}",
    connected: "{{name}} connected",
    disconnected: "{{name}} disconnected",
    statusConnected: "Connected",
    statusDisconnected: "Not connected",
    statusUnavailable: "Not available yet",
    unavailableHint: "This integration isn't available yet. CRUD.IT will enable it for your workspace.",
    fields: {
      spreadsheet_id: "Spreadsheet ID",
      sheet_name: "Sheet name",
      portal_id: "Portal ID",
      pipeline_id: "Pipeline ID",
      calendar_id: "Calendar ID",
      default_duration_min: "Default booking length (minutes)",
    },
    zoho: settingsZoho,
    google: {
      name: "Google Calendar",
      description: "Lets the assistant offer real free times and add confirmed bookings to your calendar.",
      connect: "Connect Google Calendar",
      redirecting: "Redirecting to Google…",
      connectNote: "You'll sign in with Google and allow {{product}} to read free/busy times and add events. You can disconnect any time.",
      unavailable: "Ask the platform team to set up Google sign-in.",
      notConfigured: "Google sign-in isn't set up for this platform yet. Ask the platform team.",
      connectedAs: "Connected as {{email}}",
      calendar: "Booking calendar",
      calendarHint: "Confirmed bookings are added here, and busy times on it block slots.",
      calendarLoading: "Loading your calendars",
      calendarError: "We couldn't load your calendars from Google.",
      calendarEmpty: "This Google account has no calendar you can add events to.",
      primary: "{{name}} (primary)",
      meet: "Add a Google Meet link to new bookings",
      meetHint: "Calendar bookings get a video link. You can also turn this on for a single booking action.",
      saveCalendar: "Save calendar",
      calendarSaved: "Booking calendar updated",
      disconnectTitle: "Disconnect Google Calendar?",
      disconnectBody: "{{product}} stops reading your calendar and creating events. Bookings that visitors request afterwards wait for your team to confirm. Events already in your calendar stay there.",
      disconnectConfirm: "Disconnect Google Calendar",
      disconnected: "Google Calendar disconnected",
      callback: {
        docTitle: "Connecting Google Calendar",
        title: "Connecting Google Calendar",
        loading: "Finishing the connection with Google…",
        connected: "Google Calendar connected. Choose where bookings go.",
        failedTitle: "We couldn't finish connecting Google Calendar",
        failedBody: "Start again from Settings → Integrations.",
        deniedTitle: "Google sign-in was cancelled",
        deniedBody: "Nothing was connected. You can try again from Settings → Integrations.",
        missingTitle: "This sign-in link has expired",
        missingBody: "Start again from Settings → Integrations so we can match it to your workspace.",
        back: "Back to Integrations",
        backHome: "Back to console",
      },
    },
    sheets: {
      name: "Google Sheets",
      description: "Every new lead is added as a row in a Google Sheet your team can open.",
      connect: "Connect Google Sheets",
      redirecting: "Redirecting to Google…",
      connectNote: "You'll sign in with Google. {{product}} creates a new spreadsheet for your leads and can only open the files it creates, never the rest of your Drive.",
      unavailable: "Ask the platform team to set up Google sign-in.",
      notConfigured: "Google sign-in isn't set up for this platform yet. Ask the platform team.",
      connectedTo: "New leads go to “{{title}}”.",
      open: "Open spreadsheet",
      disconnectTitle: "Disconnect Google Sheets?",
      disconnectBody: "{{product}} stops adding new leads to the spreadsheet. The spreadsheet and the leads already in it stay in your Google Drive.",
      disconnectConfirm: "Disconnect Google Sheets",
      disconnected: "Google Sheets disconnected",
      callback: {
        docTitle: "Connecting Google Sheets",
        title: "Connecting Google Sheets",
        loading: "Creating your leads spreadsheet…",
        connected: "Google Sheets connected. New leads will be added to your spreadsheet.",
        failedTitle: "We couldn't finish connecting Google Sheets",
        failedBody: "Start again from Settings → Integrations.",
        deniedTitle: "Google sign-in was cancelled",
        deniedBody: "Nothing was connected. You can try again from Settings → Integrations.",
        missingTitle: "This sign-in link has expired",
        missingBody: "Start again from Settings → Integrations so we can match it to your workspace.",
        back: "Back to Integrations",
        backHome: "Back to console",
      },
    },
  },
  security: {
    title: "Sign-in rules",
    require2fa: "Require 2FA for all members",
    membersWithout2fa_one: "{{count}} member hasn't set up 2FA yet and will be asked on next sign-in.",
    membersWithout2fa_other: "{{count}} members haven't set up 2FA yet and will be asked on next sign-in.",
    allHave2fa: "Every member has set up 2FA.",
    domains: "Allowed email domains",
    domainsHint: "Only these domains can be invited as team members. Leave empty to allow any.",
    domainsPlaceholder: "example.ph",
    domainsAdd: "Add",
    domainsRemove: "Remove {{domain}}",
    domainsEmpty: "Any email domain can be invited",
    domainsCount: "{{count}} of {{max}}",
    domainsInvalid: "Enter a domain like example.ph, without spaces, http:// or a path.",
    domainsDuplicate: "{{domain}} is already in the list.",
    domainsFull: "You can add up to {{max}} domains. Remove one to add another.",
    domainsAdded: "{{domain}} added",
    domainsRemoved: "{{domain}} removed",
    timeout: "Session timeout",
    timeouts: {
      "30m": "30 minutes of inactivity",
      "8h": "8 hours (one work day)",
      "7d": "7 days",
      "30d": "30 days",
    },
    ipAllowlist: "IP allowlist",
    ipPlaceholder: "One IP or CIDR per line",
    ipHint: "Leave empty to allow sign-in from anywhere (up to {{count}} entries).",
    ipInvalid: "Check these entries: {{items}}",
    saved: "Security settings saved",
    samlTitle: "SAML single sign-on",
    proPlan: "Pro plan",
    samlBody: "Single sign-on for team members is on the way. End-user sign-in is under",
    samlLink: "Audiences → Identity providers",
    samlToggle: "Enable SAML",
    sso: {
      title: "Single sign-on",
      body: "Let your team sign in with your company identity provider, over SAML 2.0 or OpenID Connect.",
      lockedTitle: "Single sign-on is included in Pro",
      lockedBody: "Upgrade to Pro to connect SAML or OpenID Connect and to require company sign-in for your domains.",
      upgrade: "See plans",
      emptyTitle: "No identity provider connected",
      emptyBody: "Connect Okta, Microsoft Entra ID, Google Workspace or any SAML 2.0 or OpenID Connect provider. Members keep their password until you turn on enforcement.",
      connect: "Connect identity provider",
      protocol: "Protocol",
      saml: "SAML 2.0",
      oidc: "OpenID Connect",
      providerSection: "Identity provider",
      spTitle: "Give these to your identity provider",
      spEntityId: "Service provider entity ID",
      spAcs: "Assertion consumer service URL",
      spMetadata: "Service provider metadata URL",
      spRedirect: "Redirect URI",
      copy: "Copy",
      copied: "Copied",
      metadata: "Identity provider metadata",
      metadataHint: "Upload the metadata XML file your identity provider gives you.",
      metadataUpload: "Upload metadata file",
      metadataLoaded: "Loaded {{name}}",
      metadataTooLarge: "That file is larger than 256 KB.",
      issuer: "Issuer",
      issuerHint: "For example https://login.example.com. We read the other addresses from its discovery document.",
      clientId: "Client ID",
      clientSecret: "Client secret",
      clientSecretSaved: "A secret is saved. Leave this empty to keep it.",
      domains: "Company e-mail domains",
      domainsHint: "Only addresses on these domains sign in through your provider. Up to {{count}}.",
      domainAdd: "Add domain",
      domainPlaceholder: "company.com",
      domainInvalid: "Enter a domain such as company.com.",
      certificates: "Signing certificates",
      certExpires: "expires {{date}}",
      save: "Save connection",
      saved: "Single sign-on saved",
      remove: "Remove connection",
      removed: "Single sign-on removed",
      removeTitle: "Remove single sign-on?",
      removeBody: "Members sign in with their password again and enforcement turns off.",
      test: "Test sign-in",
      testHint: "Sign in once through your provider with your own account. You need a passing test before you can require single sign-on.",
      testOk: "Test sign-in succeeded.",
      testFailed: "Test sign-in failed: {{reason}} Check the settings and try again.",
      tested: "Last tested {{date}}",
      neverTested: "Not tested yet",
      enforceTitle: "Require single sign-on",
      enforceBody: "Password sign-in is turned off for the domains above, except for the break-glass owner.",
      enforceNeedsTest: "Run a successful test sign-in first.",
      breakGlass: "Break-glass owner",
      breakGlassHint: "This owner keeps password sign-in in case your provider is unavailable.",
      unsaved: "Save the connection to run a test.",
      loadError: "Single sign-on could not load.",
      reasons: {
        failed: "the provider answer was not accepted.",
        unavailable: "single sign-on is not available for this workspace.",
        not_a_member: "that account is not a member of this workspace.",
        domain: "that e-mail domain is not listed.",
        expired: "the sign-in took too long. Start again.",
      },
    },
  },
  privacy: {
    noTrainingTitle: "Do not use our data to train models — guaranteed",
    noTrainingBody: "Your documents and conversations are never used to train our or any AI provider's models. We use zero-retention API settings with our model providers. This is written into your Data Processing Agreement.",
    retentionTitle: "Retention",
    keepConversations: "Keep conversations for",
    keepHint: "Leads you exported to a CRM are not affected.",
    keepAttachments: "Keep uploaded attachments for",
    days_one: "{{count}} day",
    days_other: "{{count}} days",
    piiTitle: "PII redaction defaults",
    piiBody: "Masked before messages are stored or shown to staff. The AI still sees the text to answer.",
    pii: {
      mobile: "Mobile numbers",
      email: "Email addresses",
      national_id: "National ID and member numbers",
      card_bank: "Card and bank account numbers",
      address: "Home addresses",
      gov_id: "Government IDs (PhilSys, TIN, SSS)",
    },
    saved: "Privacy settings saved",
    complianceTitle: "Compliance documents",
    complianceBody: "Your organization is the personal information controller; CRUD.IT Solutions Inc. is the processor under the Data Privacy Act of 2012 (RA 10173).",
    dpa: "Data Processing Agreement",
    subprocessors: "List of subprocessors",
    npcNote: "Remember to list this service in your NPC registration and privacy notice, and name your Data Protection Officer.",
    exportTitle: "Export all data",
    exportBody: "Download every document, conversation, lead and setting as a ZIP (JSON + original files). Useful for data subject access requests and audits.",
    exportAction: "Export all data",
    exportStarted: "Export started. We'll email {{email}} when the ZIP is ready.",
    exportRunning: "Preparing export…",
    exportFailed: "Last export failed",
    exportDownload: "Download export ({{date}})",
    exportExpired: "The last export has expired. Start a new one.",
    dangerTitle: "Danger zone",
    deleteHeading: "Delete this workspace",
    deleteSummary: "Permanently deletes all agents, knowledge sources, conversations and leads after a 14-day grace period. This can't be undone.",
    deleteAction: "Delete workspace",
    deleteTitle: "Delete {{workspace}}?",
    deleteBody: "All agents stop answering immediately. Data is permanently erased after 14 days. The Owner can cancel during that time.",
    deleteConfirm: "Type {{workspace}} to confirm",
    deleteScheduled: "{{workspace}} is scheduled for deletion on {{date}}. Cancel anytime before then.",
    deletePending: "Scheduled for deletion on {{date}}",
    deletePendingBody: "Agents have stopped answering. Cancel to restore the workspace.",
    deleteCancel: "Cancel deletion",
    deleteCancelled: "Deletion cancelled. The workspace is active again.",
  },
  audit: {
    title: "Audit log",
    subtitle: "Every change made in this workspace · kept for 1 year",
    filter: "Filter by person, action or target…",
    since: "From date",
    until: "To date",
    export: "Export CSV",
    colTime: "Time",
    colActor: "Actor",
    colAction: "Action",
    colTarget: "Target",
    colIp: "IP address",
    system: "System",
    viaSupport: "(via CRUD.IT support)",
    filteredTo: "Filtered to {{name}}",
    oneMember: "one member",
    clearActor: "Clear member filter",
    loadMore: "Load more",
    emptyTitle: "No matching activity",
    emptyBody: "Try a different search or date range.",
  },
  notifications: {
    title: "Notifications",
    subtitle: "Choose what you hear about and where. Applies to you in this workspace.",
    colType: "Notification",
    colOn: "On",
    channels: {
      in_app: "In app",
      email: "Email",
    },
    enableOne: "{{type}} notifications",
    channelOne: "{{type}} by {{channel}}",
    required: "Always on",
    saved: "Notification preferences saved",
    types: {
      handoff_requested: "Handoff requests",
      source_processed: "Source ready",
      source_failed: "Source failed",
      usage_threshold: "Usage alerts",
      payment_failed: "Payment problems",
      weekly_report: "Weekly report",
      knowledge_gap: "Knowledge gaps",
      eval_regression: "Evaluation regressions",
      lead_assigned: "Lead assigned to you",
      new_lead: "New leads",
      new_booking: "New bookings",
      workflow: "Workflow alerts",
      security: "Security alerts",
      team: "Team changes",
    },
    hints: {
      handoff_requested: "A visitor asked for a person.",
      source_processed: "A document finished processing.",
      source_failed: "A document couldn't be processed.",
      usage_threshold: "Messages or storage are close to the plan limit.",
      payment_failed: "A charge didn't go through.",
      weekly_report: "A summary of conversations, resolution and gaps every Monday.",
      knowledge_gap: "Visitors keep asking something the assistant can't answer.",
      eval_regression: "An evaluation run scored lower than before.",
      lead_assigned: "A new or reassigned lead needs follow-up.",
      new_lead: "Someone shared their contact details with the assistant.",
      new_booking: "A visitor booked a time, or asked for one that needs your confirmation.",
      workflow: "A workflow you set up notified you.",
      security: "New sign-ins, 2FA and password changes.",
      team: "Invites accepted and role changes.",
    },
  },
  domains: {
    title: "Custom domain",
    subtitle: "Serve the web app on your own address, e.g. ask.yourdomain.com",
    hostname: "Domain",
    hostnameHint: "Use a subdomain such as ask.yourdomain.com. You'll add DNS records next.",
    placeholder: "ask.example.com",
    invalid: "Enter a subdomain like ask.example.com",
    agent: "Agent",
    agentHint: "Pin the domain to one agent, or show all public agents.",
    allAgents: "All public agents",
    add: "Add domain",
    added: "{{host}} added. Add the DNS records below, then verify.",
    recordsHint: "Changes usually take a few minutes and can take up to an hour. We check every hour; use Verify to check now.",
    recordsTitle_one: "Add this record at your DNS provider",
    recordsTitle_other: "Add these records at your DNS provider",
    noRecords: "No DNS change is needed right now. We are waiting for the certificate.",
    colProxy: "Proxy",
    colStatus: "Status",
    copyName: "Copy the name of the {{type}} record",
    copyValue: "Copy the value of the {{type}} record",
    proxyDnsOnly: "DNS only",
    proxyNone: "Not applicable",
    recordFound: "Found",
    recordMissing: "Not found yet",
    tip: {
      cloudflareTitle: "Your DNS is on Cloudflare.",
      cloudflareBody: "Set the record's proxy status to DNS only (grey cloud). With the orange cloud on, the certificate can't be issued. If {{host}} already points at Cloudflare's proxy, an existing record for it, or a * record, needs changing.",
    },
    progress: {
      label: "Set-up progress for {{host}}",
      added: "Domain added",
      dns: "DNS record found",
      certificate: "Certificate issued",
      live: "Live",
      state: { done: "done", pending: "waiting", failed: "failed" },
      reached: "Since {{when}}",
      checked: "Not yet. Checked {{when}}",
      notChecked: "Not checked yet",
      failedHint: "The hosting provider no longer knows this domain. Remove it and add it again.",
    },
    root: {
      title: "Chat opens at the root address",
      on: "On",
      body: "{{url}} opens the chat. Visitors never land on the console sign-in.",
      open: "Open",
      openLabel: "Open the chat at {{host}}",
    },
    branding: {
      title: "Hide “Powered by {{product}}”",
      body: "Removes the footer on the hosted chat and sign-in pages. Change it in Widget Studio, under Appearance.",
      lockedBody: "Available on Pro and Institution plans. Upgrade to remove the footer.",
      shown: "Shown",
      hidden: "Hidden",
      locked: "Locked",
      open: "Open Widget Studio",
      upgrade: "Upgrade",
    },
    botCheck: {
      title: "Bot check on this address",
      added: "Allowed",
      manual: "One manual step",
      addedBody: "{{host}} is on the bot check's allowed hostnames, so visitors can sign in and chat here.",
      manualBody: "We could not add {{host}} to the bot check's allowed hostnames automatically. Ask Sagot support to add it, then use Verify to check again. Until then the chat on this address may show a verification error.",
    },
    recordsLabel: "DNS records for {{host}}",
    colType: "Type",
    colName: "Name",
    colValue: "Value",
    verify: "Verify",
    verified: "{{host}} is verified",
    notYet: "We can't see the DNS records yet. Try again in a few minutes.",
    remove: "Remove",
    removed: "Domain removed",
    lastChecked: "Last checked {{when}}",
    neverChecked: "Not checked yet",
    sslActive: "SSL active",
    sslPending: "SSL pending",
    status: {
      pending: "Waiting for DNS",
      verifying: "Verifying",
      active: "Verified",
      failed: "Verification failed",
    },
    emptyTitle: "No custom domain yet",
    emptyBody: "Add one so visitors chat on your own address instead of the default link.",
  },
  setup: {
    title: "Industry & goals",
    subtitle: "Shapes suggested templates, follow-up questions and the organization profile the assistant knows about.",
    save: "Save industry & goals",
    saved: "Industry & goals saved",
    resuggest: "Re-suggest setup",
    resuggestTitle: "Re-suggest setup",
    resuggestBody: "Get a fresh suggested setup from your industry, goals and description. Nothing changes until you choose to create a new assistant.",
    target: "Suggest for",
    targetNew: "A new assistant",
    describe: "Describe what you want (optional)",
    suggest: "Suggest",
    diffTitle: "What would change on {{name}}",
    noDiff: "This assistant already matches the suggestion.",
    current: "Current",
    suggested: "Suggested",
    neverOverwrite: "Existing assistants are never overwritten. Copy what you like into the agent's settings, or create a new assistant from this suggestion.",
    createNew: "Create as new assistant",
    created: "{{name}} created",
    fields: {
      assistant_name: "Assistant name",
      instructions: "Persona & scope",
      starter_questions: "Starter questions",
      welcome_message: "Welcome message",
      audiences: "Audiences",
    },
    summaryStages: "Lead pipeline: {{stages}}",
    summaryStarters: "Starter questions: {{starters}}",
  },
} as const;
